export interface LessonMoveNode {
  id: string | number;
  san?: string;
  fen?: string;
  annotation?: string | null;
  annotation_icon?: string | null;
  success_message?: string | null;
  hint?: string | null;
  deviation_message?: string | null;
  child_moves?: LessonMoveNode[];
  [key: string]: any;
}

// Branches deliberately authored with one of these glyphs are wrong
// alternatives (with their own comment explaining why) rather than
// acceptable variations — matches how Lichess gamebook chapters use NAGs to
// mark a move as a mistake vs. a fine alternative.
const BAD_ANNOTATION_ICONS = new Set(['Blunder', 'Mistake', 'Dubious move']);

export function isBadMove(move: LessonMoveNode | undefined | null): boolean {
  return (
    !!move?.annotation_icon && BAD_ANNOTATION_ICONS.has(move.annotation_icon)
  );
}

/**
 * Given the mainline (with branches nested under child_moves) and the node
 * the student is currently on, returns the candidate next moves split into
 * "good" (the mainline continuation plus any branch not authored as a
 * Blunder/Mistake/Dubious move) and "bad" (branches deliberately authored as
 * a wrong alternative).
 */
export function getNextMoveOptions(
  currentMoves: LessonMoveNode[],
  moveSelected: LessonMoveNode | null | undefined,
): {good: LessonMoveNode[]; bad: LessonMoveNode[]} {
  if (!moveSelected || moveSelected.id === 'start') {
    return {
      good: currentMoves.filter(m => !isBadMove(m)),
      bad: currentMoves.filter(isBadMove),
    };
  }

  const candidates: LessonMoveNode[] = [];
  const idx = currentMoves.findIndex(m => m.id === moveSelected.id);
  if (idx !== -1 && idx + 1 < currentMoves.length) {
    candidates.push(currentMoves[idx + 1]);
  }

  const findNode = (nodes: LessonMoveNode[]): LessonMoveNode | null => {
    for (const node of nodes) {
      if (node.id === moveSelected.id) {
        return node;
      }
      if (node.child_moves?.length) {
        const found = findNode(node.child_moves);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  const node = idx !== -1 ? currentMoves[idx] : findNode(currentMoves);
  if (node?.child_moves) {
    candidates.push(...node.child_moves);
  }

  return {
    good: candidates.filter(m => !isBadMove(m)),
    bad: candidates.filter(isBadMove),
  };
}

export function isOnMainline(
  currentMoves: LessonMoveNode[],
  moveSelected: LessonMoveNode | null | undefined,
): boolean {
  if (!moveSelected || moveSelected.id === 'start') {
    return true;
  }
  return currentMoves.some(m => m.id === moveSelected.id);
}

/**
 * Once a variation the student is playing through runs out of authored
 * moves, we don't want to end the lesson there - the mainline is the "real"
 * line, and a variation is just an acceptable detour off it. This finds
 * where the student left the mainline, so the caller can rewind the board
 * there and prompt the student to find the actual mainline continuation
 * instead of celebrating the end of a side branch.
 *
 * Walks moveSelected's `parent` chain up until it hits a node that's in the
 * flat mainline array (or the game's start, if the student diverged on the
 * very first move) - handles a variation nested inside another variation,
 * not just a single level of branching.
 */
export function getMainlineBranchPoint(
  currentMoves: LessonMoveNode[],
  moveSelected: LessonMoveNode | null | undefined,
): {rewindTo: LessonMoveNode | 'start'} | null {
  if (isOnMainline(currentMoves, moveSelected) || !moveSelected) {
    return null;
  }

  const nodeById = new Map<string | number, LessonMoveNode>();
  const indexNodes = (nodes: LessonMoveNode[]) => {
    for (const n of nodes) {
      nodeById.set(n.id, n);
      if (n.child_moves?.length) {
        indexNodes(n.child_moves);
      }
    }
  };
  indexNodes(currentMoves);

  let parentId = moveSelected.parent;
  while (parentId != null) {
    const mainlineMatch = currentMoves.find(m => m.id === parentId);
    if (mainlineMatch) {
      return {rewindTo: mainlineMatch};
    }
    const ancestor = nodeById.get(parentId);
    if (!ancestor) {
      break;
    }
    parentId = ancestor.parent;
  }
  return {rewindTo: 'start'};
}
